import moment from "moment";
import { ResourceSkill } from "@app/models/rest/resource-skill";
import { ResourceZone } from "@app/models/rest/resource-zone";

/**
 * Client-side mirror of the solver's conditional skill/zone mapping rules
 * (ResourceAvailabilityConverter.ConditionalRule on the server), generalised
 * from a single shift instant to the schedule view's time range: an assignment
 * is "active" when its validity period and, if set, one of its mapping-pattern
 * windows overlap [viewStart, viewEnd). Used to decide which skills/zones the
 * Schedule view shows for the time range in view (#1558).
 */

// Structural types: REST models arrive as ProjectionRelType-wrapped objects,
// so only the data fields the evaluation needs are declared here.
interface PatternEntryLike {
  offsetDay?: number;
  startOffsetHour?: number;
  startOffsetMinute?: number;
  duration?: number;
}

interface PatternLike {
  length?: number;
  patternOrigin?: Date | string;
  patternEntries?: PatternEntryLike[];
}

interface ConditionalAssignment {
  startDate?: Date | string;
  endDate?: Date | string;
  mappingPattern?: PatternLike;
}

export function isAssignmentActiveInRange(
  assignment: ConditionalAssignment,
  viewStart: Date | null,
  viewEnd: Date | null
): boolean {
  if (!assignment) {
    return false;
  }
  // No view range yet — nothing to evaluate against, show everything.
  if (!viewStart || !viewEnd) {
    return true;
  }

  // The view range is half-open [viewStart, viewEnd), so an assignment
  // starting exactly at viewEnd has no overlap. Assignment endDate is
  // inclusive, matching the server (shiftStart.isAfter(endDate) rejects
  // only strictly-after), so endDate === viewStart still counts as active.
  if (
    assignment.startDate &&
    moment(assignment.startDate).isSameOrAfter(viewEnd)
  ) {
    return false;
  }
  if (assignment.endDate && moment(assignment.endDate).isBefore(viewStart)) {
    return false;
  }

  return isPatternActiveInRange(assignment.mappingPattern, viewStart, viewEnd);
}

function isPatternActiveInRange(
  pattern: PatternLike,
  viewStart: Date,
  viewEnd: Date
): boolean {
  if (!pattern) {
    return true; // unconditional within the validity window
  }
  // Pattern data incomplete (e.g. loaded without entries inlined) — fail open
  // rather than hide a skill/zone the solver may still apply.
  if (!pattern.length || !pattern.patternOrigin || !pattern.patternEntries) {
    return true;
  }

  const originDay = moment(pattern.patternOrigin).startOf("day");
  const rangeStart = moment(viewStart);
  const rangeEnd = moment(viewEnd);
  // Start one day early so windows crossing midnight into the view still count.
  const day = moment(viewStart).startOf("day").subtract(1, "day");
  const lastDay = moment(viewEnd).startOf("day");

  while (day.isSameOrBefore(lastDay)) {
    const totalDays = day.diff(originDay, "days");
    if (totalDays >= 0) {
      const effectiveDay = totalDays % pattern.length;
      for (const entry of pattern.patternEntries) {
        if (
          entry.offsetDay !== effectiveDay ||
          entry.offsetDay === null ||
          entry.offsetDay === undefined ||
          entry.startOffsetHour === null ||
          entry.startOffsetHour === undefined ||
          entry.startOffsetMinute === null ||
          entry.startOffsetMinute === undefined ||
          entry.duration === null ||
          entry.duration === undefined
        ) {
          continue;
        }
        const windowStart = moment(day)
          .add(entry.startOffsetHour, "hours")
          .add(entry.startOffsetMinute, "minutes");
        const windowEnd = moment(windowStart).add(entry.duration, "minutes");
        if (windowStart.isBefore(rangeEnd) && windowEnd.isAfter(rangeStart)) {
          return true;
        }
      }
    }
    day.add(1, "day");
  }
  return false;
}

export function getEffectiveSkills(
  skillsList: ResourceSkill[],
  viewStart: Date | null,
  viewEnd: Date | null
): ResourceSkill[] {
  return (skillsList || []).filter(
    (resourceSkill) =>
      resourceSkill?.skill &&
      isAssignmentActiveInRange(resourceSkill, viewStart, viewEnd)
  );
}

export function getEffectiveZones(
  zonesList: ResourceZone[],
  viewStart: Date | null,
  viewEnd: Date | null
): ResourceZone[] {
  return (zonesList || []).filter(
    (resourceZone) =>
      resourceZone &&
      isAssignmentActiveInRange(resourceZone, viewStart, viewEnd)
  );
}

const caseInsensitive = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** Sorted display text for a set of (already effective) skill assignments. */
export function buildSkillText(skills: ResourceSkill[]): string {
  return (skills || [])
    .map((resourceSkill) => resourceSkill.skill?.description)
    .filter((description) => !!description)
    .sort(caseInsensitive)
    .join(", ");
}

/** Sorted display text for a set of (already effective) zone assignments. */
export function buildZoneText(zones: ResourceZone[]): string {
  return (zones || [])
    .map((zone: any) => zone.zone?.name || zone.zoneName)
    .filter((name) => !!name)
    .sort(caseInsensitive)
    .join(", ");
}
